import { useContext, useEffect, useRef } from 'react';
import { folder, Leva, useControls } from 'leva';
import { Schema } from 'leva/dist/declarations/src/types';
import { IsMobileContext } from 'contexts/IsMobile';
import { desktopTheme } from 'components/common/LevaThemes';
import { StateMachineInput } from 'types/rive';
import {
  buildLegacyInputs,
  buildName,
  buildViewModelProperties,
} from 'components/common/leva_common';
import featureFlags from 'lib/featureFlags';
import {
  EventCallback,
  EventType,
  Rive,
  StateMachineInputType,
  ViewModelInstance,
  ViewModelInstanceBoolean,
  ViewModelInstanceColor,
  ViewModelInstanceEnum,
  ViewModelInstanceNumber,
  ViewModelInstanceString,
} from '@rive-app/react-webgl2';

type Props = {
  rive: Rive | null;
  inputs: StateMachineInput[] | null;
};

export default function LevaMarketplaceControls({ rive, inputs }: Props) {
  const { isMobilePortrait: isMobile } = useContext(IsMobileContext);
  const viewModelInstanceRef = useRef<ViewModelInstance | null>(null);

  const [, set] = useControls(() => {
    const controls: Schema = {};

    if (rive && inputs) {
      const inputsSchema = buildLegacyInputs(inputs);
      controls.inputs = folder(inputsSchema, { order: 1 });
    }

    if (featureFlags.dataBinding()) {
      const viewModelInstance = rive?.viewModelInstance;
      if (viewModelInstance) {
        viewModelInstanceRef.current = viewModelInstance;
        controls.data = folder(
          buildViewModelProperties(viewModelInstance, 0, 'default'),
          { order: 2 }
        );
      }
    }

    return controls;
  }, [rive, inputs]);

  /// Update legacy inputs
  useEffect(() => {
    if (!rive || !set || !inputs) {
      return;
    }

    const propsToInputs = new Map<string, StateMachineInput>();
    inputs.forEach((input) => {
      const label = input.name.toLowerCase().replaceAll(' ', '_');
      switch (input.type) {
        case StateMachineInputType.Boolean:
          propsToInputs.set(label, input);
          break;
        case StateMachineInputType.Trigger:
          break;
        case StateMachineInputType.Number:
          propsToInputs.set(label, input);
          break;
      }
    });

    function setDisplayValues() {
      for (const [key, input] of propsToInputs) {
        if (input.value !== undefined) {
          set({ [key]: input.value });
        }
      }
    }

    function addAdvanceListener() {
      rive?.on(EventType.Advance, setDisplayValues);
    }

    function removeAdvanceListener() {
      rive?.off(EventType.Advance, setDisplayValues);
    }

    addAdvanceListener();

    return () => removeAdvanceListener();
  }, [rive, set, inputs]);

  /// Update view model properties
  useEffect(() => {
    if (!viewModelInstanceRef.current || !set) {
      return;
    }

    const viewModelInstance = viewModelInstanceRef.current;
    const instanceName = 'default';

    const propsToFunctions = new Map<string, EventCallback>();
    function addViewModelListeners(
      viewModelInstance: ViewModelInstance,
      set: Function,
      level: number
    ) {
      if (!set || !viewModelInstance) {
        return;
      }
      viewModelInstance.properties.forEach((property) => {
        const path = buildName(property.name, level, instanceName);
        switch (property.type.toString()) {
          case 'number':
            {
              const prop = viewModelInstance.number(property.name);
              const action = (val: any) => {
                set({ [path]: val });
              };
              propsToFunctions.set(path, action);
              prop?.on(action);
            }
            break;
          case 'string':
            {
              const prop = viewModelInstance.string(property.name);
              const action = (val: any) => {
                set({ [path]: val });
              };
              propsToFunctions.set(path, action);
              prop?.on(action);
            }
            break;
          case 'boolean':
            {
              const prop = viewModelInstance.boolean(property.name);
              const action = (val: any) => {
                set({ [path]: val });
              };
              propsToFunctions.set(path, action);
              prop?.on(action);
            }
            break;
          case 'enumType':
            {
              const prop = viewModelInstance.enum(property.name);
              const action = (val: any) => {
                set({ [path]: val });
              };
              propsToFunctions.set(path, action);
              prop?.on(action);
            }
            break;
          case 'color':
            {
              const prop = viewModelInstance.color(property.name);
              const action = (val: any) => {
                const a = (val >> 24) & 0xff;
                const r = (val >> 16) & 0xff;
                const g = (val >> 8) & 0xff;
                const b = val & 0xff;
                set({
                  [path]: {
                    r: r,
                    g: g,
                    b: b,
                    a: a / 255,
                  },
                });
              };
              propsToFunctions.set(path, action);
              prop?.on(action);
            }
            break;
          case 'viewModel':
            {
              const viewModelProp = viewModelInstance.viewModel(property.name);
              if (viewModelProp) {
                addViewModelListeners(viewModelProp, set, level + 1);
              }
            }
            break;
        }
      });
    }

    function removeViewModelListeners(
      viewModelInstance: ViewModelInstance,
      set: Function,
      level: number
    ) {
      if (!set || !viewModelInstance) {
        return;
      }
      viewModelInstance.properties.forEach((property) => {
        const name = buildName(property.name, level, instanceName);
        const action = propsToFunctions.get(name);
        switch (property.type.toString()) {
          case 'number':
            {
              const prop = viewModelInstance.number(
                property.name
              ) as ViewModelInstanceNumber;
              prop?.off(action as EventCallback);
            }
            break;
          case 'string':
            {
              const prop = viewModelInstance.string(
                property.name
              ) as ViewModelInstanceString;
              prop?.off(action as EventCallback);
            }
            break;
          case 'boolean':
            {
              const prop = viewModelInstance.boolean(
                property.name
              ) as ViewModelInstanceBoolean;
              prop?.off(action as EventCallback);
            }
            break;
          case 'enumType':
            {
              const prop = viewModelInstance.enum(
                property.name
              ) as ViewModelInstanceEnum;
              prop?.off(action as EventCallback);
            }
            break;
          case 'color':
            {
              const prop = viewModelInstance.color(
                property.name
              ) as ViewModelInstanceColor;
              prop?.off(action as EventCallback);
            }
            break;
          case 'viewModel':
            {
              const viewModelProp = viewModelInstance.viewModel(property.name);
              if (viewModelProp) {
                removeViewModelListeners(viewModelProp, set, level + 1);
              }
            }
            break;
        }
      });
    }
    addViewModelListeners(viewModelInstance, set, 0);

    return () => {
      removeViewModelListeners(viewModelInstance, set, 0);
    };
  }, [viewModelInstanceRef.current, set]);

  return (
    <Leva
      flat
      fill
      hideCopyButton
      titleBar={false}
      collapsed={isMobile}
      theme={{ ...desktopTheme }}
    />
  );
}
